import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import LearningPage from './vnext/learn/LearningPage';
import VideoCaseDetailPage from './vnext/learn/VideoCaseDetailPage';
import LearningPrototypePage from './vnext/learn/LearningPrototypePage';
import HelpCenterPage from './vnext/help/HelpCenterPage';
import AccountCenterPage from './vnext/account/AccountCenterPage';
import AccountRouteFrame from './vnext/account/AccountRouteFrame';
import LegalDocumentPage from './vnext/legal/LegalDocumentPage';
import TeamInvitationAcceptPage from './vnext/account/TeamInvitationAcceptPage';
import WritingProjectsPage from './vnext/writing/WritingProjectsPage';
import CanvasProjectsPage from './vnext/canvas/CanvasProjectsPage';
import XiaoyingLoading from './vnext/components/XiaoyingLoading';
import CreativeAgentPage from './pages/CreativeAgentPage';
import PrototypeCreativeAgent from './pages/PrototypeCreativeAgent';
import PrototypeAgentStatusPage from './pages/PrototypeAgentStatusPage';
import PrototypeCanvasPage from './pages/PrototypeCanvasPage';
import PrototypeWritingPage from './pages/PrototypeWritingPage';
import ScriptDetail from './pages/ScriptDetail';
import AssetManagerPage from './pages/AssetManagerPage';
import TemplateLibraryPage from './vnext/templates/TemplateLibraryPage';
import Admin from './pages/Admin';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import useTheme from './hooks/useTheme';
import WebsiteVersionNavigation from './vnext/components/WebsiteVersionNavigation';

const CanvasPage = lazy(() => import('./pages/CanvasPage'));
const CanvasVNextPage = lazy(() => import('./vnext/canvas/CanvasVNextPage'));
const TemplatePreviewPage = lazy(() => import('./vnext/templates/TemplatePreviewPage'));
const WritingWorkbenchPage = lazy(() => import('./vnext/writing/WritingWorkbenchPage'));

function CanvasRouteFallback() {
  return <XiaoyingLoading label="正在打开画布" detail="把创意、素材和节点接回原位" />;
}

function CanvasRoute({ vnext = false, preview = false }) {
  const Component = vnext ? CanvasVNextPage : CanvasPage;
  return <Suspense fallback={<CanvasRouteFallback />}><Component preview={preview} /></Suspense>;
}

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const location = useLocation();
  const outletContext = useOutletContext();
  const target = `${location.pathname}${location.search}${location.hash}`;
  return token
    ? (children || <Outlet context={outletContext} />)
    : <Navigate to="/login" replace state={{ from: target }} />;
};

export default function App() {
  useTheme();
  return (
    <BrowserRouter>
      <WebsiteVersionNavigation />
      <AccountRouteFrame renderAccount={location => <Routes location={location}>
        <Route path="/prototype/vnext" element={<Layout preview accountOverlay />}>
          <Route path="*" element={<AccountCenterPage />} />
        </Route>
        <Route path="/" element={<PrivateRoute><Layout accountOverlay /></PrivateRoute>}>
          <Route path="*" element={<AccountCenterPage />} />
        </Route>
      </Routes>}>
      {backgroundLocation => <Routes location={backgroundLocation}>
        <Route path="/prototype/canvas" element={<PrototypeCanvasPage />} />
        <Route path="/prototype/vnext/canvas/preview-canvas" element={<CanvasRoute vnext preview />} />
        <Route path="/prototype/vnext/canvas/:id" element={<PrivateRoute><CanvasRoute vnext /></PrivateRoute>} />
        <Route path="/prototype/agent-status" element={<PrototypeAgentStatusPage />} />
        <Route path="/writing-preview" element={<PrototypeWritingPage />} />
        <Route path="/prototype/vnext" element={<Layout preview />}>
          <Route index element={<Home />} />
          <Route path="learn" element={<LearningPrototypePage />} />
          <Route path="learn/cases/:caseId" element={<LearningPrototypePage view="detail" />} />
          <Route path="learn/cases/:caseId/workflow" element={<LearningPrototypePage view="workflow" />} />
          <Route path="help" element={<HelpCenterPage />} />
          <Route path="help/:section" element={<HelpCenterPage />} />
          <Route path="writing" element={<WritingProjectsPage />} />
          <Route path="writing/:projectId" element={<Suspense fallback={<XiaoyingLoading label="正在打开写作台" detail="章节、人物和上次写到的位置正在归位" />}><WritingWorkbenchPage preview /></Suspense>} />
          <Route path="canvas" element={<CanvasProjectsPage />} />
          <Route path="assets" element={<AssetManagerPage preview />} />
          <Route path="templates" element={<TemplateLibraryPage preview />} />
          <Route path="templates/:templateId" element={<Suspense fallback={<XiaoyingLoading label="正在打开模板" detail="只读画布正在展开" />}><TemplatePreviewPage preview /></Suspense>} />
          <Route path="account/:section" element={<AccountCenterPage />} />
          <Route path="settings/:section" element={<AccountCenterPage />} />
          <Route path="billing/:section" element={<AccountCenterPage />} />
          <Route path="team" element={<AccountCenterPage />} />
          <Route path="team/:section" element={<AccountCenterPage />} />
        </Route>
        <Route path="/login"    element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/terms" element={<LegalDocumentPage />} />
        <Route path="/privacy" element={<LegalDocumentPage />} />
        <Route path="/team/invite/:inviteId" element={<TeamInvitationAcceptPage />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Navigate to="/home" />} />
          <Route path="home"        element={<Home />} />
          <Route path="studio"      element={<Navigate to="/home" replace />} />
          <Route path="learn"       element={<LearningPage />} />
          <Route path="learn/:caseId" element={<VideoCaseDetailPage />} />
          <Route path="help"        element={<HelpCenterPage />} />
          <Route path="help/:section" element={<HelpCenterPage />} />
          <Route element={<PrivateRoute />}>
            <Route path="creative-agent" element={<CreativeAgentPage />} />
            <Route path="prototype/creative-agent" element={<PrototypeCreativeAgent />} />
            <Route path="prototype/agent-status" element={<PrototypeAgentStatusPage />} />
            <Route path="prototype/canvas" element={<PrototypeCanvasPage />} />
            <Route path="director"    element={<Navigate to="/writing" replace />} />
            <Route path="writing"     element={<WritingProjectsPage />} />
            <Route path="writing/:projectId" element={<Suspense fallback={<XiaoyingLoading label="正在打开写作台" detail="章节、人物和上次写到的位置正在归位" />}><WritingWorkbenchPage /></Suspense>} />
            <Route path="writing-legacy" element={<Navigate to="/writing" replace />} />
            {/* 「剧本管理」整页已砍：审核评分并入写剧本 tab，旧全部剧本/上传新建/版本记录下线 */}
            <Route path="script-manager" element={<Navigate to="/writing" replace />} />
            <Route path="library"     element={<Navigate to="/writing" replace />} />
            <Route path="scripts"     element={<Navigate to="/writing" replace />} />
            <Route path="scripts/:id" element={<ScriptDetail />} />
            <Route path="assets"      element={<AssetManagerPage />} />
            <Route path="templates"   element={<TemplateLibraryPage />} />
            <Route path="templates/:templateId" element={<Suspense fallback={<XiaoyingLoading label="正在打开模板" detail="只读画布正在展开" />}><TemplatePreviewPage /></Suspense>} />
            <Route path="image"       element={<Navigate to="/assets" replace />} />
            <Route path="agent-lab"   element={<Navigate to="/writing" replace />} />
            <Route path="video"       element={<Navigate to="/assets?tab=videos" replace />} />
            <Route path="canvas"       element={<CanvasProjectsPage />} />
            <Route path="canvas/:id"  element={<CanvasRoute vnext />} />
            <Route path="deliveries"  element={<Navigate to="/assets?tab=delivery" replace />} />
            <Route path="review"      element={<Navigate to="/writing?tab=review" replace />} />
            <Route path="profile"     element={<Navigate to="/account/profile" replace />} />
            <Route path="account/:section" element={<AccountCenterPage />} />
            <Route path="settings/:section" element={<AccountCenterPage />} />
            <Route path="billing/:section" element={<AccountCenterPage />} />
            <Route path="team" element={<AccountCenterPage />} />
            <Route path="team/:section" element={<AccountCenterPage />} />
          </Route>
        </Route>
        <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
          <Route index element={<Admin />} />
        </Route>
        <Route path="/prototype/admin" element={<AdminLayout preview />}>
          <Route index element={<Admin preview />} />
        </Route>
      </Routes>}
      </AccountRouteFrame>
    </BrowserRouter>
  );
}
